import {useEffect} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {zodResolver} from "@hookform/resolvers/zod";
import {SubmitHandler, useForm} from "react-hook-form";
import {z} from "zod";
import {
    Form,
    FormControl, FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import {Input} from "@/components/ui/input";
import {Textarea} from "@/components/ui/textarea";
import {Button} from "@/components/ui/button";
import {useToast} from "@/components/ui/use-toast";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card.tsx";
import {getMentorById, updateMentorWithMetadata} from "@/services/mentorService.ts";
import {Mentor, UpdateMentorMetaDto} from "@/types/Mentor.ts";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {queryKeys} from "@/services/queryKeyFactory.ts";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select.tsx";
import {regionOptions} from "@/utils/profile-info.ts";
import {Loader2} from "lucide-react";
import {useTranslation} from "react-i18next";
import {activeLocale, LocalizedField} from "@/utils/translation-helper.ts";
import {fillTranslatedFields} from "@/services/translateService.ts";

const MentorProfileUpdateGeneral = () => {
    const {t} = useTranslation();
    const {t: vt} = useTranslation("validation");
    const {id} = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const {toast} = useToast();

    const {data: mentor} = useQuery({
        queryKey: queryKeys.fetchMentors.one,
        queryFn: () => getMentorById(id ? parseInt(id) : -1),
    });

    const generalFormSchema = z.object({
        title: z.string().trim().min(1, {
            message: vt("enter", {fieldName: t("mentor.title")}),
        }),
        titleFr: z.string().trim().min(1, {
            message: vt("enter", {fieldName: t("mentor.title")}),
        }),
        university: z.string().trim().min(1, {
            message: vt("enter", {fieldName: t("mentor.university")}),
        }),
        universityFr: z.string().trim().min(1, {
            message: vt("enter", {fieldName: t("mentor.university")}),
        }),
        bio: z.string().trim().min(1, {
            message: vt("enter", {fieldName: t("mentor.bio")}),
        }),
        bioFr: z.string().trim().min(1, {
            message: vt("enter", {fieldName: t("mentor.bio")}),
        }),
        region: z.string().trim().min(1, {
            message: vt("enter", {fieldName: t("mentor.region")}),
        }),
        regionFr: z.string().trim().min(1, {
            message: vt("enter", {fieldName: t("mentor.region")}),
        }),
    }).partial();

    type ProfileFormValues = z.infer<typeof generalFormSchema>;

    const form = useForm<ProfileFormValues>({
        resolver: zodResolver(generalFormSchema),
        mode: "onChange",
    });

    useEffect(() => {
        if (mentor) {
            if (activeLocale() === "fr") {
                form.reset({
                    titleFr: mentor.titleFr ?? "",
                    universityFr: mentor.universityFr ?? "",
                    bioFr: mentor.bioFr ?? "",
                    regionFr: mentor.regionFr ?? ""
                });
            } else {
                form.reset({
                    title: mentor.title ?? "",
                    university: mentor.university ?? "",
                    bio: mentor.bio ?? "",
                    region: mentor.region ?? ""
                });
            }
        }
    }, [mentor]);

    const onRequestError = () => {
        toast({
            variant: 'destructive',
            title: t("common.errorTitle"),
            description: t("common.errorDesc"),
        });
    };

    const onUpdateSuccess = async (mentor: Mentor) => {
        navigate(`/mentors/${mentor.id}`);
        await queryClient.setQueryData(queryKeys.fetchMentors.one, () => {
            return mentor;
        });
        toast({
            description: t("profile.updateSuccess"),
        });
    };

    const updateMutation = useMutation({
        mutationFn: ({id, ...dto}: Partial<UpdateMentorMetaDto> & { id: number }) => updateMentorWithMetadata(id, dto),
        onSuccess: onUpdateSuccess,
        onError: onRequestError,
    });

    const onSubmit: SubmitHandler<z.infer<typeof generalFormSchema>> = async (values: ProfileFormValues) => {
        if (!mentor) return;

        const dirtyFields = form.formState.dirtyFields as Partial<Record<keyof ProfileFormValues, boolean>>;
        const userTextPairs = [
            { sourceKey: "title" as const, targetKey: "titleFr" as const },
            { sourceKey: "university" as const, targetKey: "universityFr" as const },
            { sourceKey: "bio" as const, targetKey: "bioFr" as const },
        ];

        const userTextPairsFrToEn = [
            { sourceKey: "titleFr" as const, targetKey: "title" as const },
            { sourceKey: "universityFr" as const, targetKey: "university" as const },
            { sourceKey: "bioFr" as const, targetKey: "bio" as const },
        ];

        try {
            if (activeLocale() === "fr") {
                const translated = await fillTranslatedFields({
                    pairs: userTextPairsFrToEn,
                    values: values as Record<string, string | undefined>,
                    dirtyFields,
                    existing: {
                        title: mentor.title ?? undefined,
                        university: mentor.university ?? undefined,
                        bio: mentor.bio ?? undefined,
                    },
                    sourceLocale: "fr",
                    targetLocale: "en",
                });
                const updateDto = {
                    titleFr: values.titleFr,
                    universityFr: values.universityFr,
                    bioFr: values.bioFr,
                    regionFr: values.regionFr,
                    region: regionOptions.find(region => region.fr === values.regionFr)?.en,
                    ...translated,
                };
                updateMutation.mutate({ ...updateDto, id: mentor.id });
            } else {
                const translated = await fillTranslatedFields({
                    pairs: userTextPairs,
                    values: values as Record<string, string | undefined>,
                    dirtyFields,
                    existing: {
                        titleFr: mentor.titleFr ?? undefined,
                        universityFr: mentor.universityFr ?? undefined,
                        bioFr: mentor.bioFr ?? undefined,
                    },
                    sourceLocale: "en",
                    targetLocale: "fr",
                });
                const updateDto = {
                    title: values.title,
                    university: values.university,
                    bio: values.bio,
                    region: values.region,
                    regionFr: regionOptions.find(region => region.en === values.region)?.fr,
                    ...translated,
                };
                updateMutation.mutate({ ...updateDto, id: mentor.id });
            }
        } catch (err) {
            toast({
                variant: "destructive",
                title: t("common.errorTitle"),
                description: err instanceof Error ? err.message : t("common.errorDesc"),
            });
        }
    };

    return (
        <Card className="border border-border bg-bg-card shadow-sm">
            <CardHeader className="border-b border-border">
                <CardTitle className="text-xl font-semibold text-text-1">{t("profile.generalInformation")}</CardTitle>
                <CardDescription className="text-text-2">
                    {t("profile.generalInformationDesc")}
                </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                    <FormField
                        control={form.control}
                        name={activeLocale() === "fr" ? "titleFr" : "title"}
                        render={({field}) => (
                            <FormItem>
                                <FormLabel>{t("mentor.title")}</FormLabel>
                                <FormControl>
                                    <Input {...field} />
                                </FormControl>
                                <FormMessage/>
                            </FormItem>
                        )}
                    />

                    <FormField
                        name={activeLocale() === "fr" ? "universityFr" : "university"}
                        control={form.control}
                        render={({field}) => (
                            <FormItem>
                                <FormLabel>{t("mentor.university")}</FormLabel>
                                <FormControl>
                                    <Input {...field} />
                                </FormControl>
                                <FormMessage/>
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name={activeLocale() === "fr" ? "regionFr" : "region"}
                        render={({field}) => (
                            <FormItem>
                                <FormLabel>{t("mentor.region")}</FormLabel>
                                <Select onValueChange={field.onChange}
                                        value={field.value}
                                >
                                    <FormControl>
                                        <SelectTrigger>
                                            <SelectValue placeholder={t("mentor.selectRegion")}/>
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent className="max-w-[360px] sm:max-w-fit overflow-auto">
                                        {regionOptions.map((region, idx) => (
                                            <SelectItem key={idx} value={region[activeLocale() as keyof LocalizedField]}>
                                                {region[activeLocale() as keyof LocalizedField]}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <FormDescription>

                                </FormDescription>
                                <FormMessage/>
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name={activeLocale() === "fr" ? "bioFr" : "bio"}
                        render={({field}) => (
                            <FormItem>
                                <FormLabel>{t("mentor.bio")}</FormLabel>
                                <FormControl>
                                    <Textarea
                                        placeholder={t("mentor.bioPlaceholder")}
                                        className="resize-none"
                                        {...field}
                                    />
                                </FormControl>
                                <FormDescription>
                                    {t("mentor.bioDesc")}
                                </FormDescription>
                                <FormMessage/>
                            </FormItem>
                        )}
                    />
                    <Button
                        type="submit"
                        disabled={!form.formState.isDirty || updateMutation.isPending}
                    >
                        {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin"/>}
                        {t("profile.updateProfile")}
                    </Button>
                    </form>
                </Form>
            </CardContent>
        </Card>
    );
}

export default MentorProfileUpdateGeneral;
